import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { RestService } from '../services/rest.service';
import { MappingTelemetryService } from '../services/mapping-telemetry.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { BrandService } from '../../services/brand-config.service';
import { TestInProgressComponent } from '../guards/test-in-progress.guard';
import { loaderComponent } from '../shared/components/loader/loader.component';
import { CheckinProgressComponent } from '../shared/components/checkin-progress/checkin-progress.component';

/**
 * SENSE — Sensory Signal Check (vision + hearing).
 *
 * The test itself is a self-contained interactive app (Web Audio API + canvas +
 * adaptive staircases) shipped as a static asset under `assets/sense-app/`. We
 * host it in an isolated iframe so its psychophysics/timing logic runs exactly
 * as designed with no CSS/JS bleed. To make it fit the app shell (it renders
 * under the real app header like the cognitive test) we fetch the asset HTML and
 * inject brand + language + chrome overrides before handing it to the iframe via
 * `srcdoc`. The asset hands its completed result set back via `postMessage`;
 * this component persists it (authenticated) against the current scan and then
 * continues to the report — mirroring `CognitiveComponent`.
 */
@Component({
  selector: 'app-sense',
  standalone: true,
  imports: [CommonModule, TranslatePipe, loaderComponent, CheckinProgressComponent],
  templateUrl: './sense.component.html',
  styleUrls: ['./sense.component.scss'],
})
export class SenseComponent
  implements OnInit, OnDestroy, TestInProgressComponent
{
  @ViewChild('frame') frame?: ElementRef<HTMLIFrameElement>;

  scanId = '';
  srcdoc?: SafeHtml;
  saving = false;
  // QA render-only preview (route data.preview): no scanId, nothing persists,
  // "continue" re-runs the check. Lets Danna walk the flow with no login/scan.
  preview = false;

  private started = false;
  private completed = false;
  private readonly msgHandler = (e: MessageEvent) => this.onMessage(e);

  /**
   * Autosave. The check is two modules and the vision one is a full minute of
   * ladder trials, but until now nothing left the iframe until BOTH were done -
   * so a phone call after the vision part threw the vision part away too. The
   * asset now hands over a `sense-progress` message when a module ends; it is
   * stored as a draft (never on scan.sense) and handed back into the iframe on
   * the next visit, which then opens straight at the hearing part.
   */
  private resume: any = null;
  private draftSaving = false;
  /** a draft older than this is dropped rather than offered: a vision reading
   *  from yesterday and a hearing reading from today are not one sitting */
  private static readonly DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private restService: RestService,
    private telemetry: MappingTelemetryService,
    private translationService: TranslationService,
    private brand: BrandService,
    private sanitizer: DomSanitizer
  ) {}

  async ngOnInit(): Promise<void> {
    this.preview = !!this.route.snapshot.data['preview'];
    this.scanId = this.route.snapshot.params['scanId'] || '';
    window.addEventListener('message', this.msgHandler);
    // If this scan's SENSE is already completed (e.g. she finished the tests,
    // got bounced to login, then pressed Back and landed here again), do NOT
    // reload the test iframe from scratch — that forces her to redo a finished
    // test. The result is safely persisted server-side; just continue to the
    // report. (Bug 7.1 — Back from login wiping SENSE.)
    if (!this.preview && this.scanId && (await this.isSenseAlreadyDone())) {
      this.router.navigate(['/app/results', this.scanId]);
      return;
    }
    await this.loadDraft();
    await this.loadAsset();
  }

  /** True if this scan's SENSE result already exists. Checks a local
   *  completion flag FIRST (set the moment the result was persisted) so the
   *  Back-from-login restore works instantly even if the server is slow/offline,
   *  then falls back to the backend. (Bug 7.1.) */
  private async isSenseAlreadyDone(): Promise<boolean> {
    try {
      if (sessionStorage.getItem(`sense_done_${this.scanId}`) === '1') {
        return true;
      }
    } catch {}
    try {
      const res: any = await this.restService.request({
        method: 'GET',
        path: `scan/${this.scanId}/new-test`,
      });
      return !!res?.data?.sense;
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.msgHandler);
  }

  // ---- autosave ------------------------------------------------------------

  /** Read this scan's sense draft (server first, local fallback) so the asset
   *  can boot at the hearing part. Best effort: no draft just means the check
   *  starts from the top, which is today's behaviour. */
  private async loadDraft(): Promise<void> {
    if (this.preview || !this.scanId) return;
    let draft: any = null;
    try {
      const res: any = await this.restService.request({
        method: 'GET',
        path: `scan/${this.scanId}/new-test/exercise-draft`,
        throwError: true,
        skipAuthRedirect: true,
      });
      draft = res?.sense || null;
    } catch {
      /* no session / offline: the local copy below is the fallback */
    }
    if (!draft) draft = this.readLocalDraft();
    if (!draft || typeof draft !== 'object') return;
    const age = Date.now() - new Date(draft.at || 0).getTime();
    if (!(age >= 0) || age > SenseComponent.DRAFT_MAX_AGE_MS) {
      this.clearLocalDraft();
      return;
    }
    this.resume = draft;
  }

  private async saveDraft(payload: any): Promise<void> {
    if (this.preview || !this.scanId || this.draftSaving) return;
    this.draftSaving = true;
    const draft = { ...payload, at: new Date().toISOString() };
    try {
      await this.restService.request({
        method: 'POST',
        path: `scan/${this.scanId}/new-test/exercise-draft`,
        body: { kind: 'sense', draft },
        throwError: true,
        skipAuthRedirect: true,
      });
      this.clearLocalDraft();
    } catch {
      this.writeLocalDraft(draft);
    } finally {
      this.draftSaving = false;
    }
  }

  private get localKey(): string {
    return `sense_draft_${this.scanId}`;
  }

  private writeLocalDraft(draft: unknown): void {
    try {
      localStorage.setItem(this.localKey, JSON.stringify(draft));
    } catch {
      /* storage blocked or full: the server copy is the real one anyway */
    }
  }

  private readLocalDraft(): any | null {
    try {
      const raw = localStorage.getItem(this.localKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private clearLocalDraft(): void {
    try {
      localStorage.removeItem(this.localKey);
    } catch {}
  }

  /** Fetch the SENSE asset and adapt it to the current brand + language. */
  private async loadAsset(): Promise<void> {
    try {
      const res = await fetch('/assets/sense-app/index.html');
      const raw = await res.text();
      this.srcdoc = this.sanitizer.bypassSecurityTrustHtml(this.adapt(raw));
    } catch (err) {
      console.error('SenseComponent: failed to load SENSE asset', err);
    }
  }

  private adapt(html: string): string {
    const rtl = this.translationService.isRTL();
    const lang = rtl ? 'he' : 'en';
    const dir = rtl ? 'rtl' : 'ltr';
    const brandName = this.brand.config.brandName;

    // Chrome overrides: blend into the app page (white, no shadow), drop the
    // asset's own top nav (the app header already provides brand + nav), so we
    // don't get a doubled header. Keep the 390px centred column.
    const overrides =
      '<style id="sense-host-overrides">' +
      'html,body{background:#fff !important;}' +
      '#app{box-shadow:none !important;min-height:100% !important;max-width:430px;}' +
      '.nav{display:none !important;}' +
      '</style>';

    // Autosave: what she already completed, handed to the asset before it boots.
    // JSON only, and only the module state the asset itself produced - it is
    // read back into its own `S`, never evaluated.
    const resumeTag = this.resume
      ? '<script>window.__SENSE_RESUME__=' +
        JSON.stringify(this.resume).replace(/</g, '\\u003c') +
        ';</script>'
      : '';

    let out = html
      .replace(/<html lang="en">/, `<html lang="${lang}" dir="${dir}">`)
      .replace(/2026 MARKABLE/g, `2026 ${brandName}`)
      .replace(/<\/head>/, `${overrides}${resumeTag}</head>`);

    // The SENSE asset is authored in Markable's brand magenta (#D1409C family +
    // its rgba shadows). Markable keeps those literals as-is; for Peritale we
    // swap them to the Peritale brand tokens so the test matches the host brand
    // instead of showing Markable purple. (Markable byte-identical = no QA churn.)
    if (!this.brand.config.isMarkable) {
      out = out
        .replace(/#D1409C/gi, '#ff0083') // --accent
        .replace(/#b8357f/gi, '#cc0066') // --accent-dark
        .replace(/#f5e0ee/gi, '#ffe6f3') // --accent-light
        .replace(/#fdf5fa/gi, '#fff0f6') // --accent-bg
        .replace(/rgba\(\s*209\s*,\s*64\s*,\s*156\s*,/gi, 'rgba(255,0,131,')
        // secondary violet accent on the hearing/vision intro cards
        .replace(/#7b26a2/gi, '#ff0083')
        .replace(/#f3e8ff/gi, '#ffe6f3')
        .replace(/rgba\(\s*123\s*,\s*38\s*,\s*162\s*,/gi, 'rgba(255,0,131,');
    }

    // The SENSE v2 asset is bilingual-native: it reads document.documentElement.lang
    // (set above) and renders he/en itself, so the old DOM-translator (SENSE_HE_DICT
    // + MutationObserver) is no longer injected - it would double-translate.
    return out;
  }

  get isRtl(): boolean {
    return this.translationService.isRTL();
  }

  private onMessage(e: MessageEvent): void {
    // Only trust messages coming from our own embedded SENSE iframe.
    const frameWin = this.frame?.nativeElement?.contentWindow;
    if (!frameWin || e.source !== frameWin) return;

    const data = e.data;
    if (!data || typeof data !== 'object') return;

    if (data.type === 'sense-started') {
      this.started = true;
      this.telemetry.setAssessment(this.scanId);
      this.telemetry.step('sensory');
      this.telemetry.track('sensory_activity_started');
    } else if (data.type === 'sense-progress') {
      // a module finished: keep it, so an interruption from here costs the
      // module in flight at most, never the part she already did
      void this.saveDraft(data.payload);
    } else if (data.type === 'sense-complete' && !this.completed) {
      this.completed = true;
      this.persist(data.payload);
    } else if (data.type === 'sense-continue') {
      this.goResults();
    }
  }

  /**
   * In progress once the user enters the test proper (past the intro) and until
   * results are submitted. The in-test state lives inside the iframe, so it
   * signals start via `postMessage`; completion sets `completed`. Guarded by
   * `testInProgressGuard` so a back press can't silently wipe the run.
   */
  isTestInProgress(): boolean {
    return this.started && !this.completed;
  }

  private async persist(payload: unknown): Promise<void> {
    if (!this.scanId) return;
    // section 43: the statuses of the two modules, never the thresholds
    const p = payload as any;
    this.telemetry.track('sensory_activity_completed', {
      vision: p?.vision_contrast?.status,
      hearing: p?.hearing_tone?.status,
      headphones: p?.context?.headphones_reported === true,
    });
    // the sensory check is the last thing in the mapping
    this.telemetry.completeMapping();
    this.saving = true;
    this.telemetry.beginProcessing('sense_save');
    try {
      await this.restService.request({
        method: 'POST',
        path: `scan/${this.scanId}/new-test`,
        body: { sense: payload, senseMeta: { version: 'sense-2.1' } },
      });
      // Local completion marker so a Back-from-login reinit restores instantly
      // without waiting on (or being defeated by) a slow/failed server check.
      try {
        sessionStorage.setItem(`sense_done_${this.scanId}`, '1');
      } catch {}
      // the finished check replaces the draft (the server drops its own copy in
      // the same write)
      this.clearLocalDraft();
    } catch (err) {
      console.error('SenseComponent: save failed', err);
      this.telemetry.track('sensory_save_failed');
    } finally {
      this.saving = false;
      this.telemetry.endProcessing('sense_save');
    }
  }

  private goResults(): void {
    if (this.preview) {
      // QA preview: re-run the check in place instead of leaving to a report.
      this.completed = false;
      this.started = false;
      this.loadAsset();
      return;
    }
    if (this.scanId) {
      this.router.navigate(['/app/results', this.scanId]);
    } else {
      this.router.navigate(['/app/new-test']);
    }
  }
}
